// @flow
import { AdSlot, SeoData, SortableTable, } from '@haaretz/htz-components';
import React, { Fragment, } from 'react';
import { FelaTheme, } from 'react-fela';

import type { Node, } from 'react';

import PageRow from '../../components/PageRow/PageRow';
import RowItem from '../../components/RowItem/RowItem';
import MainLayout from '../../layouts/MainLayout';
import useAllAdUnits from '../../components/AdManager/AdUnitsProvider';
import useAdUnitPathTemplate, { createAdSlotProps, } from '../../components/AdManager/useAdUnitPathTemplate';

type Props = {
  url: {
    pathname: string,
    query: {
      section: string,
    },
    asPath: string,
  },
};

const numToString: number => string = num => num.toLocaleString('he', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

function arbitrage({
  url: {
    query: { section, },
    asPath,
  },
}: Props): Node {
  const adUnits = useAllAdUnits();
const adUnitPathTemplate = useAdUnitPathTemplate();

  return (
    <MainLayout section={section}>
      <SeoData
        title="ארביטראז'-מניות דואליות - TheMarker Finance"
        description={"כל המידע על  ארביטראז נתוני מסחר, פערי ארביטראז' , מניות ישראליות בחו\"ל , מניות דואליות , חדשות ועוד באתר TheMarker Finance"}
        path={asPath}
      />
      <FelaTheme
        render={theme => (
          <Fragment>
            <PageRow>
              <RowItem title="מניות ארביטראז׳">
                <SortableTable
                  miscStyles={{
                    marginTop: '7rem',
                    tableLayout: 'auto',
                  }}
                  filters={[ { section: 'index', subSection: '-2000', }, ]}
                  loadMore
                  type="stocks"
                  fragment="
                      dualName
                      symbol
                      dualID
                      arbGap
                      dualValue
                      USDValue
                      changePercentage
                      dualChangePercentage
                     "
                  fields={[
                    {
                      name: 'dualName',
                      display: 'שם מניה (בישראל)',
                      sortingOrder: 'asc',
                      style: () => ({
                        fontWeight: '700',
                        maxWidth: '17rem',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }),
                      value: ({ dualName, }) => dualName,
                      path: { type: 'type', id: 'dualID', },
                    },
                    {
                      name: 'symbol',
                      display: 'סימול בוול סטריט',
                      sortingOrder: 'asc',
                      value: ({ symbol, }) => symbol,
                      path: { type: 'type', id: 'id', },
                    },
                    {
                      name: 'arbGap',
                      display: 'פער ארביטראז\'',
                      sortingOrder: 'desc',
                      style: ({ arbGap, }) => ({
                        ...(
                          arbGap
                            ? {
                              color:
                                arbGap < 0
                                  ? theme.color('negative')
                                  : theme.color('positive'),
                            }
                            : {}
                        ),
                        direction: 'ltr',
                        fontWeight: '700',
                        paddingEnd: '2rem',
                        position: 'relative',
                        textAlign: 'start',
                      }),
                      value: ({ arbGap, }) => `
                        ${arbGap > 0 ? '' : arbGap < 0 ? '-' : ''}
                        ${arbGap ? `${numToString(Math.abs(arbGap))}%` : '-'}
                      `,
                    },
                    {
                      name: 'dualValue',
                      display: 'שער בישראל',
                      sortingOrder: 'desc',
                      hideUntil: [ 'l', ],
                      value: ({ dualValue, }) => (dualValue ? numToString(dualValue) : '-'),
                    },
                    {
                      name: 'USDValue',
                      display: 'שער בוול סטריט',
                      sortingOrder: 'desc',
                      hideUntil: [ 'l', ],
                      value: ({ USDValue, }) => (USDValue ? numToString(USDValue) : '-'),
                    },
                    {
                      name: 'dualChangePercentage',
                      display: 'שינוי בוול סטריט',
                      sortingOrder: 'desc',
                      hideUntil: [ 'l', ],
                      style: ({ dualChangePercentage, }) => ({
                        ...(
                          dualChangePercentage
                            ? {
                              color:
                                dualChangePercentage < 0
                                  ? theme.color('negative')
                                  : theme.color('positive'),
                            }
                            : {}
                        ),
                        direction: 'ltr',
                        fontWeight: '700',
                        paddingEnd: '2rem',
                        position: 'relative',
                        textAlign: 'start',
                      }),
                      value: ({ dualChangePercentage, }) => `
                        ${dualChangePercentage > 0 ? '' : dualChangePercentage < 0 ? '-' : ''}
                        ${dualChangePercentage ? `${numToString(Math.abs(dualChangePercentage))}%` : '-'}
                      `,
                    },
                    {
                      name: 'changePercentage',
                      display: 'שינוי בישראל',
                      sortingOrder: 'desc',
                      hideUntil: [ 'l', ],
                      style: ({ changePercentage, }) => ({
                        ...(
                          changePercentage
                            ? {
                              color:
                                changePercentage < 0
                                  ? theme.color('negative')
                                  : theme.color('positive'),
                            }
                            : {}
                        ),
                        direction: 'ltr',
                        fontWeight: '700',
                        paddingEnd: '2rem',
                        position: 'relative',
                        textAlign: 'start',
                      }),
                      value: ({ changePercentage, }) => `
                        ${changePercentage > 0 ? '' : changePercentage < 0 ? '-' : ''}
                        ${changePercentage ? `${numToString(Math.abs(changePercentage))}%` : '-'}
                      `,
                    },
                  ]}
                  initialSort="arbGap"
                  count={100}
                />
              </RowItem>
            </PageRow>
            <PageRow>
              <RowItem>
                <AdSlot {...createAdSlotProps(adUnits['finance.themarker.com.banner1'], adUnitPathTemplate)} />
              </RowItem>
            </PageRow>
          </Fragment>
        )}
      />
    </MainLayout>
  );
}

export default arbitrage;
